/**
 * Chart Configuration Library
 * 
 * Centralized chart definitions to eliminate duplication across:
 * - Batch.vue
 * - ConfigurationDetailsModal.vue  
 * - dbcsiPublicChartAPI Lambda
 * 
 * Usage:
 *   import { getChartConfig } from './chart_config.js';
 *   const config = getChartConfig('CPU_PDB', dataObject);
 * 
 */

/**
 * Get chart configuration by tag
 * @param {string} chartTag - Chart identifier (e.g., 'CPU_PDB', 'CPU_NONPDB')
 * @param {object} data - Data object containing database metrics and configuration
 * @param {object} options - Optional overrides (e.g., { form: formData })
 * @returns {object} Chart configuration object
 */
export function getChartConfig(chartTag, data, options = {}) {
  const configs = {
    
    // ==================== CPU CHARTS ====================
    
    CPU_PDB: (data, opts) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Average PDB CPU Usage:Peak PDB CPU Usage:Alternate Average PDB CPU Usage',
      chart_id: 'PDB_CPU',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      filter: data.IS_PDB === 'YES' ? ( data.CDB_VCPU ?? 0 ) * 2 : 0,
      filter_prompt: '(twice the p90 of CDB Peak CPU)',
      targets: 'Target',
      targetValues: data.VCPU ?? "undefined",
      adjustment: !data.PLATFORM_NAME?.includes('x86') 
        ? (data.AIX_CPU_ADJUSTMENT_USED * 1 > 0 
            ? (data.cpu_thread_ratio ?? opts.form?.cpu_thread_ratio ?? 1) * 1 / data.AIX_CPU_ADJUSTMENT_USED 
            : (data.cpu_thread_ratio ?? opts.form?.cpu_thread_ratio ?? 1))
        : 1.0,
      // Optional comparison to CDB
      compare_collection_id: data.CDB_collectionId,
      compare_ctrl_text: 'Compare to CDB Metrics',
      compare_label: 'CDB Average DB CPU Usage:CDB Peak DB CPU Usage',
      compare_chart_id: 'CPUAVGPEAK'
    }),
    
    CPU_NONPDB: (data, opts) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Average DB CPU Usage:Peak DB CPU Usage',
      chart_id: 'CPUAVGPEAK',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      filter: data.IS_PDB === 'YES' ? ( data.CDB_VCPU ?? 0 ) * 2 : 0,
      filter_prompt: '(twice the p90 of CDB Peak CPU)',
      targets: 'Target',
      targetValues: data.VCPU ?? "undefined",
      adjustment: !data.PLATFORM_NAME?.includes('x86')
        ? (data.AIX_CPU_ADJUSTMENT_USED * 1 > 0 
            ? (data.cpu_thread_ratio ?? opts.form?.cpu_thread_ratio ?? 1) * 1 / data.AIX_CPU_ADJUSTMENT_USED 
            : (data.cpu_thread_ratio ?? opts.form?.cpu_thread_ratio ?? 1))
        : 1.0
    }),
    
    // ==================== THROUGHPUT CHARTS ====================
    
    THROUGHPUT_PDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Average PDB Throughput:Peak PDB Throughput:Alternate Average PDB Throughput',
      chart_id: 'PDB_IOTHRU',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      filter: data.IS_PDB === 'YES' ? (data.CDB_IOMB??0) * 2 : 0,
      filter_prompt: '(twice the p99 of Average CDB Throughput)',
      targets: 'Target',
      targetValues: data.OrigThroughput ?? "undefined",
      adjustment: 1.0
    }),
    
    THROUGHPUT_NONPDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Average Throughput:Peak Throughput',
      chart_id: 'IO_MBS_AVGPEAK',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      filter: data.IS_PDB === 'YES' ? (data.CDB_IOMB??0) * 2 : 0,
      filter_prompt: '(twice the p99 of Average CDB Throughput)',
      targets: 'Target',
      targetValues: data.OrigThroughput ?? "undefined",
      adjustment: 1.0
    }),
    
    THROUGHPUT_EXADATA_ADJUSTED_PDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Adjusted Average PDB Throughput:Adjusted Peak PDB Throughput:Alternate Adjusted Average PDB Throughput',
      chart_id: 'PDB_IOTHRU',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      filter: data.IS_PDB === 'YES' ? (data.CDB_IOMB??0) * 2 : 0,
      filter_prompt: '(twice the p99 of Average CDB Throughput)',
      targets: `Adjusted Target - ${(data.Throughput * 1).toFixed(0)} MB/s`,
      targetValues: data.Throughput ?? "undefined",
      adjustment: data.ExadataAdjustment ?? "undefined"
    }),
    
    THROUGHPUT_EXADATA_ADJUSTED_NONPDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Adjusted Average Throughput:Adjusted Peak Throughput',
      chart_id: 'IO_MBS_AVGPEAK',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      filter: data.IS_PDB === 'YES' ? (data.CDB_IOMB??0) * 2 : 0,
      filter_prompt: '(twice the p99 of Average CDB Throughput)',
      targets: `Adjusted Target - ${(data.Throughput * 1).toFixed(0)} MB/s`,
      targetValues: data.Throughput ?? "undefined",
      adjustment: data.ExadataAdjustment ?? "undefined"
    }),
    
    // ==================== IOPS CHARTS ====================
    
    IOPS_PDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Average PDB IOPS',
      chart_id: 'PDB_IOPS',
      reflines: 'false',
      instance_num: 'all',
      filter: data.IS_PDB === 'YES' ? (data.CDB_IOPS??0) * 2 : 0,
      filter_prompt: '(twice the p99 of Average CDB IOPS)',
      targets: 'Target',
      targetValues: data.OrigIOPS ?? "undefined",
      // Optional comparison to CDB
      compare_collection_id: data.CDB_collectionId,
      compare_ctrl_text: 'Compare to CDB Metrics',
      compare_label: 'Average CDB IOPS:Peak CDB IOPS',
      compare_chart_id: 'IOPS_AVGPEAK'
    }),
    
    IOPS_NONPDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Average IOPS:Peak IOPS',
      chart_id: 'IOPS_AVGPEAK',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      targets: 'Target',
      targetValues: data.OrigIOPS ?? "undefined"
    }),
    
    IOPS_EXADATA_ADJUSTED_PDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Adjusted Average PDB IOPS',
      chart_id: 'PDB_IOPS',
      reflines: 'false',
      instance_num: 'all',
      filter: data.IS_PDB === 'YES' ? (data.CDB_IOPS??0) * 2 : 0,
      filter_prompt: '(twice the p99 of Average CDB IOPS)',
      targets: `Adjusted Target - ${(data.IOPS * 1).toFixed(0)}`,
      targetValues: data.IOPS ?? "undefined",
      adjustment: data.ExadataAdjustment ?? "undefined",
      // Optional comparison to CDB
      compare_collection_id: data.CDB_collectionId,
      compare_ctrl_text: 'Compare to CDB Metrics',
      compare_label: 'Average CDB IOPS:Peak CDB IOPS',
      compare_chart_id: 'IOPS_AVGPEAK'
    }),
    
    IOPS_EXADATA_ADJUSTED_NONPDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'Adjusted Average IOPS:Adjusted Peak IOPS',
      chart_id: 'IOPS_AVGPEAK',
      reflines: 'false',
      instance_num: 'all',
      stacked: 'false',
      targets: `Adjusted Target - ${(data.IOPS * 1).toFixed(0)}`,
      targetValues: data.IOPS ?? "undefined",
      adjustment: data.ExadataAdjustment ?? "undefined"
    }),
    
    // ==================== MEMORY CHARTS ====================
    
    MEMORY_PDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'PDB SGA:PDB PGA',
      chart_id: 'MEMORYPDB',
      stacked: 'true',
      reflines: 'false',
      instance_num: 'all',
      targets: 'Target',
      targetValues: data.Memory ?? "undefined"
    }),
    
    MEMORY_NONPDB: (data) => ({
      collection_id: data.collectionID,
      collection_type: 'ora_v1',
      label: 'SGA:PGA',
      chart_id: 'MEMORY',
      stacked: 'true',
      reflines: 'false',
      instance_num: 'all',
      targets: 'Target',
      targetValues: data.Memory ?? "undefined"
    })
  };
  
  const configFn = configs[chartTag];
  if (!configFn) {
    throw new Error(`Unknown chart tag: ${chartTag}`);
  }
  

  const config = configFn(data, options);
 // console.log("chartConfig", config.filter, data.CDB_IOPS, config);
  
  return config;
}

/**
 * Get chart configuration for ConfigurationDetailsModal.vue
 * This variant includes additional target values for Available capacity
 */
export function getChartConfigWithAvailable(chartTag, data, options = {}) {
  const baseConfig = getChartConfig(chartTag, data, options);
  
  // Add Available target line for CPU charts in ConfigurationDetailsModal
  if (chartTag === 'CPU_PDB' || chartTag === 'CPU_NONPDB') {
    const hasAdjusted = data.AdjustedVCPU;
    baseConfig.targets = hasAdjusted ? 'Target:TargetWithCaching:Available' : 'Target:Available';
    
    const availableVCPU = data.frozen_prefs?.ec2fsxn_baremetal_showdetail === 'ec2only' && data.frozen_prefs?.ec2fsxn_algo === 'Y'
      ? data.targetconfig_ec2fsxn?.shape_max_vcpu
      : data.targetconfig_rds?.shape_max_vcpu * data.targetconfig_rds?.rac_nodes;
    
    baseConfig.targetValues = hasAdjusted 
      ? `${data.VCPU}:${data.AdjustedVCPU}:${availableVCPU}`
      : `${data.VCPU}:${availableVCPU}`;
  }
  
  // Use pre-computed target labels/values for Throughput and IOPS in ConfigurationDetailsModal
  if (chartTag === 'THROUGHPUT_PDB' || chartTag === 'THROUGHPUT_NONPDB') {
    const useEC2 = data.frozen_prefs?.ec2fsxn_baremetal_showdetail === 'ec2only' && data.frozen_prefs?.ec2fsxn_algo === 'Y';
    baseConfig.targets = (useEC2 && data.targetconfig_ec2fsxn?.OrigThroughputtargetLabels) || data.OrigthroughputtargetLabels;
    baseConfig.targetValues = (useEC2 && data.targetconfig_ec2fsxn?.OrigThroughputtargetValues) || data.OrigthroughputtargetValues;
  }
  
  if (chartTag === 'THROUGHPUT_EXADATA_ADJUSTED_PDB' || chartTag === 'THROUGHPUT_EXADATA_ADJUSTED_NONPDB') {
    const useEC2 = data.frozen_prefs?.ec2fsxn_baremetal_showdetail === 'ec2only' && data.frozen_prefs?.ec2fsxn_algo === 'Y';
    baseConfig.targets = (useEC2 && data.targetconfig_ec2fsxn?.ThroughputtargetLabels) || data.ThroughputtargetLabels;
    baseConfig.targetValues = (useEC2 && data.targetconfig_ec2fsxn?.ThroughputtargetValues) || data.ThroughputtargetValues;
  }
  
  if (chartTag === 'IOPS_PDB' || chartTag === 'IOPS_NONPDB') {
    const useEC2 = data.frozen_prefs?.ec2fsxn_baremetal_showdetail === 'ec2only' && data.frozen_prefs?.ec2fsxn_algo === 'Y';
    baseConfig.targets = (useEC2 && data.targetconfig_ec2fsxn?.OrigIOPStargetLabels) || data.OrigIOPStargetLabels;
    baseConfig.targetValues = (useEC2 && data.targetconfig_ec2fsxn?.OrigIOPStargetValues) || data.OrigIOPStargetValues;
  }
  
  if (chartTag === 'IOPS_EXADATA_ADJUSTED_PDB' || chartTag === 'IOPS_EXADATA_ADJUSTED_NONPDB') {
    const useEC2 = data.frozen_prefs?.ec2fsxn_baremetal_showdetail === 'ec2only' && data.frozen_prefs?.ec2fsxn_algo === 'Y';
    baseConfig.targets = (useEC2 && data.targetconfig_ec2fsxn?.IOPStargetLabels) || data.IOPStargetLabels;
    baseConfig.targetValues = (useEC2 && data.targetconfig_ec2fsxn?.IOPStargetValues) || data.IOPStargetValues;
  }
  
  // Add Available target line for Memory charts in ConfigurationDetailsModal
  if (chartTag === 'MEMORY_PDB' || chartTag === 'MEMORY_NONPDB') {
    const hasAdjusted = data.AdjustedMemory;
    baseConfig.targets = hasAdjusted ? 'Target:TargetWithCaching:Available' : 'Target:Available';
    
    const availableMemory = data.frozen_prefs?.ec2fsxn_baremetal_showdetail === 'ec2only' && data.frozen_prefs?.ec2fsxn_algo === 'Y'
      ? data.targetconfig_ec2fsxn?.shape_memory
      : data.targetconfig_rds?.shape_memory * data.targetconfig_rds?.rac_nodes;
    
    baseConfig.targetValues = hasAdjusted 
      ? `${data.Memory}:${data.AdjustedMemory}:${availableMemory}`
      : `${data.Memory}:${availableMemory}`;
  }
  
  return baseConfig;
}

/**
 * Helper to determine which chart tag to use based on data
 */
export function getChartTag(metricType, data, adjusted) {
  const isPDB = data.IS_PDB === 'YES';
  const isExadataAdjusted = adjusted && 
                            data.Exadata === 'Yes' && 
                            data.frozen_prefs.smartscan_algo != 'ss1' && 
                            data[metricType] !== undefined;
                            
  // console.log("getChartTag", metricType, data, adjusted, isExadataAdjusted);                          
  
  const tagMap = {
    VCPU: isPDB ? 'CPU_PDB' : 'CPU_NONPDB',
    Throughput: isExadataAdjusted 
      ? (isPDB ? 'THROUGHPUT_EXADATA_ADJUSTED_PDB' : 'THROUGHPUT_EXADATA_ADJUSTED_NONPDB')
      : (isPDB ? 'THROUGHPUT_PDB' : 'THROUGHPUT_NONPDB'),
    IOPS: isExadataAdjusted
      ? (isPDB ? 'IOPS_EXADATA_ADJUSTED_PDB' : 'IOPS_EXADATA_ADJUSTED_NONPDB')
      : (isPDB ? 'IOPS_PDB' : 'IOPS_NONPDB'),
    Memory: isPDB ? 'MEMORY_PDB' : 'MEMORY_NONPDB'
  };
  
  return tagMap[metricType];
}
